import { type FC, useEffect } from 'react'
import { useSessionStorage } from 'usehooks-ts'

import { useExperimentsContext } from '../../../../experiments/components/ExperimentsContext'
import { useExperimentsPageType } from '../../../../experiments/components/ExperimentsPageTypeContext'
import { ExperimentPageType } from '../../../../experiments/ExperimentPageType'
import { useGetExperimentForTenant } from '../../../../experiments/hooks'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { type ExperimentType } from '../../../../experiments/model/Experiment.types'
import { useDisableMobileBodyScroll } from '../../../../generic-hooks'
import { usePushGtmUserDataAvailableEvent } from '../../../../gtm'
import { useHeaderSearchContext } from '../../header-search/headerSearchContext'
import { HeaderDataContextProvider } from '../../headerConfigContext'
import { type HeaderDataType } from '../../model/headerTypes'
import { HeaderBanner } from '../HeaderBanner'
import { TopHeaderStickyBanner } from '../TopHeaderStickyBanner/TopHeaderStickyBanner'

import { HeaderBackdropContextProvider } from './headerBackdropContext'
import { HeaderDesktop } from './HeaderDesktop'
import { HeaderMobile } from './HeaderMobile'

interface HeaderProps {
  headerData: HeaderDataType
}

const useActivateHeaderStickyToggleExperiment = (
  headerStickyToggleExperiment: ExperimentType,
): void => {
  const [
    headerStickyToggleExperimentActive,
    setHeaderStickyToggleExperimentActive,
  ] = useSessionStorage(headerStickyToggleExperiment.name, 0, {
    initializeWithValue: false,
  })
  const { addExperimentKeysForActivation } = useExperimentsContext()
  const pageType = useExperimentsPageType()

  useEffect(() => {
    if (
      !headerStickyToggleExperiment.isEnabled ||
      pageType === ExperimentPageType.Login
    ) {
      return
    }

    const handleScroll = (): void => {
      if (!headerStickyToggleExperimentActive) {
        addExperimentKeysForActivation(headerStickyToggleExperiment.name)
        setHeaderStickyToggleExperimentActive(1)
      }
    }

    window.addEventListener('scroll', handleScroll, {
      once: true,
      passive: true,
    })

    return (): void => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [
    addExperimentKeysForActivation,
    headerStickyToggleExperiment.isEnabled,
    headerStickyToggleExperiment.name,
    headerStickyToggleExperimentActive,
    setHeaderStickyToggleExperimentActive,
    pageType,
  ])
}

export const Header: FC<HeaderProps> = ({ headerData }) => {
  const headerSearchContext = useHeaderSearchContext()
  usePushGtmUserDataAvailableEvent()
  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )

  /**
   * We need to take portalResultsTo into consideration to cover an edge case since we use searchIsActive for both mobile and desktop
   * Edge case: open search on mobile, resize to desktop and open search there, resize back to mobile -> the body scroll is not working
   * because the searchIsActive is still true from the desktop version but so the portalResultsTo
   */
  useDisableMobileBodyScroll(
    headerSearchContext.searchIsActive && !headerSearchContext.portalResultsTo,
  )

  useActivateHeaderStickyToggleExperiment(headerStickyToggleExperiment)

  return (
    <>
      <HeaderBanner />
      <HeaderBackdropContextProvider>
        <HeaderDataContextProvider headerData={headerData}>
          <HeaderDesktop />
          <HeaderMobile />
          <TopHeaderStickyBanner />
        </HeaderDataContextProvider>
      </HeaderBackdropContextProvider>
    </>
  )
}
